const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const revealSelector = [
  '.content-for-layout > .shopify-section',
  'footer .shopify-section',
  '.product-card',
  '.collection-card',
  '.featured-blog-posts-card',
  '.resource-card',
  '.blog-post-card',
  '.landing-card-grid__card',
  '.landing-materials__item',
  '.landing-process__step',
  '.landing-faq__item',
].join(',');

const excludedSelector = [
  '#header-group',
  'dialog',
  '[hidden]',
  '[aria-hidden="true"]',
  '[data-scroll-reveal-ignore]',
  '.predictive-search-dropdown',
  '.quick-add-modal',
  '.cart-drawer',
  '.search-modal',
].join(',');

const readyClass = 'scroll-reveal-enabled';
const revealClass = 'scroll-reveal';
const visibleClass = 'scroll-reveal--visible';
let intersectionObserver;
let mutationFrame;

function allowsMotion() {
  return !motionQuery.matches;
}

/**
 * @param {Element} element
 */
function isEligible(element) {
  return element instanceof HTMLElement && !element.closest(excludedSelector);
}

/**
 * @param {ParentNode | Element} root
 */
function getRevealElements(root) {
  const elements = [];

  if (root instanceof Element && root.matches(revealSelector)) {
    elements.push(root);
  }

  root.querySelectorAll?.(revealSelector).forEach((element) => {
    if (isEligible(element)) elements.push(element);
  });

  return [...new Set(elements)].filter(isEligible);
}

/**
 * @param {Element} element
 */
function revealElement(element) {
  element.classList.add(visibleClass);
  intersectionObserver?.unobserve(element);
}

function revealAll() {
  document.documentElement.classList.remove(readyClass);
  intersectionObserver?.disconnect();
  intersectionObserver = undefined;

  getRevealElements(document).forEach((element) => {
    element.classList.remove(revealClass);
    element.classList.add(visibleClass);
    element.removeAttribute('data-scroll-reveal');
    element.style.removeProperty('--scroll-reveal-delay');
  });
}

function getObserver() {
  if (intersectionObserver) return intersectionObserver;

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealElement(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.12,
    }
  );

  return intersectionObserver;
}

/**
 * @param {ParentNode | Element} [root]
 */
function initScrollReveal(root = document) {
  if (!allowsMotion() || !('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  document.documentElement.classList.add(readyClass);

  const observer = getObserver();
  getRevealElements(root).forEach((element, index) => {
    if (element.hasAttribute('data-scroll-reveal')) return;

    element.setAttribute('data-scroll-reveal', 'fade-up');
    element.classList.add(revealClass);
    element.style.setProperty('--scroll-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
    observer.observe(element);
  });
}

function initMutationObserver() {
  const targets = [document.querySelector('.content-for-layout'), document.querySelector('footer')].filter(Boolean);

  if (!targets.length || !('MutationObserver' in window)) return;

  const mutationObserver = new MutationObserver((mutations) => {
    const roots = mutations.flatMap((mutation) => [...mutation.addedNodes]).filter((node) => node instanceof Element);

    if (!roots.length || mutationFrame) return;

    mutationFrame = window.requestAnimationFrame(() => {
      roots.forEach((root) => initScrollReveal(root));
      mutationFrame = undefined;
    });
  });

  targets.forEach((target) => {
    mutationObserver.observe(target, { childList: true, subtree: true });
  });
}

function init() {
  initScrollReveal();
  initMutationObserver();
}

function handleMotionChange() {
  if (allowsMotion()) {
    initScrollReveal();
    return;
  }

  revealAll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

if ('addEventListener' in motionQuery) {
  motionQuery.addEventListener('change', handleMotionChange);
} else {
  motionQuery.addListener(handleMotionChange);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initScrollReveal(event.target);
});

document.addEventListener('shopify:section:select', (event) => {
  if (!(event.target instanceof Element)) return;

  getRevealElements(event.target).forEach(revealElement);
});
